import { Check, Close } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type {
    ICertificateDurationResponse,
    IRequestCertificateDurationEvent,
} from '@sharedInterfaces/websocket/ICertificateDuration';
import changeMapCertificate from '@src/APIs/graphQl/Certificate/changeMapCertificate';
import createAndMapCertificate from '@src/APIs/graphQl/Certificate/createAndMapCertificate';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import Button from '@src/App/Components/Buttons/Button/Button';
import Dialog from '@src/App/Components/Dialogs/Dialog/Dialog';
import { ErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import DateInput from '@src/App/Components/General/FormularElements/formsControls/inputs/DateInput/DateInput';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import { setCompanyAllCertificates } from '@store/reducer/companyReducer';
import { setEmployeeCertificates } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import Row from '../../General/FormularElements/Row/Row';
import RowElement from '../../General/FormularElements/RowElement/RowElement';
import { useDialog } from '../DialogManager';

export interface IMapCertificateDialogResult {
    success: boolean;
    startDate?: Date;
    endDate?: Date;
    durationInDays?: number;
}

interface mapCertificateDialogModalProps {
    id: string;
    certId?: number;
    title?: string;
    mode?: 'full' | 'datesOnly';
    resolve: (val: IMapCertificateDialogResult) => void;
}
export const MapCertificateDialog: React.FC<mapCertificateDialogModalProps> = ({
    id,
    certId,
    title,
    mode = 'full',
    resolve,
}) => {
    const { openDialog } = useDialog();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;
    const employee = useSelector((state: AppState) => state.employee);
    const own = employee?.certificates.find((oc) => oc.id === certId);
    const offline = useSelector((state: AppState) => state.client.offline);
    const allCertificates = useSelector((state: AppState) => state.company.allCertificates);

    const [saving, setSaving] = React.useState<boolean>(false);
    const [stateStartDate, setStateStartDate] = React.useState<undefined | Date>(own?.startDate || new Date());
    const [stateEndDate, setStateEndDate] = React.useState<undefined | Date>(own?.endDate);
    const [aiGeneratedDuration, setAiGeneratedDuration] = React.useState<number | null>(null);
    const [loadingDuration, setLoadingDuration] = React.useState<boolean>(false);
    const allCert = allCertificates.find((c) => c.id === certId);
    const [isOpen, setIsOpen] = useState(true);
    const requestKeyRef = useRef<string | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setStateStartDate(own?.startDate || new Date());
        setStateEndDate(own?.endDate);
    }, [own]);

    // AI Duration Generation for new certificates (no ID)
    useEffect(() => {
        if (certId !== undefined || !title) return;

        setLoadingDuration(true);
        const requestKey = uuidv4();
        requestKeyRef.current = requestKey;

        const subscriptionKey = WebSocketClient.subscribe(
            EWebsocketTopics.generateCertificateDuration,
            (response: ICertificateDurationResponse) => {
                if (response.requestKey === requestKey) {
                    setLoadingDuration(false);
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    if (response.durationInDays !== null) {
                        setAiGeneratedDuration(response.durationInDays);
                    }
                }
            },
        );

        const request: IRequestCertificateDurationEvent = {
            certificateTitle: title,
            requestKey,
        };
        WebSocketClient.sendMessage(EWebsocketTopics.generateCertificateDuration, request);

        timeoutRef.current = setTimeout(() => {
            setLoadingDuration(false);
        }, 10000);

        return () => {
            WebSocketClient.unsubscribe(subscriptionKey);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [certId, title]);

    // Auto-calculate end date based on duration
    useEffect(() => {
        if (stateStartDate === own?.startDate) return;

        // For existing certificates (with ID): use durationInDays from database
        if (allCert?.durationInDays && stateStartDate) {
            const newDate = new Date(stateStartDate);
            newDate.setDate(newDate.getDate() + allCert.durationInDays);
            setStateEndDate(newDate);
        }
        // For new certificates (no ID): use AI-generated duration
        else if (certId === undefined && aiGeneratedDuration && stateStartDate) {
            const newDate = new Date(stateStartDate);
            newDate.setDate(newDate.getDate() + aiGeneratedDuration);
            setStateEndDate(newDate);
        }
    }, [stateStartDate, own?.startDate, allCert?.durationInDays, aiGeneratedDuration, certId]);

    const add = useCallback(() => {
        if (!title) return;
        if (!stateStartDate) return;
        if (saving || !employee) return;

        createAndMapCertificate(title, stateStartDate, stateEndDate, aiGeneratedDuration || undefined)
            .then((result) => {
                const newCerts = [...employee.certificates];
                newCerts.push({ id: result, title: title, startDate: stateStartDate, endDate: stateEndDate });
                dispatch(setEmployeeCertificates(newCerts));
                const newAllCerts = [...allCertificates];
                newAllCerts.push({ title: title, id: result, otherNames: [], version: 0 });
                dispatch(setCompanyAllCertificates(newAllCerts));

                resolve({ success: true, startDate: stateStartDate, endDate: stateEndDate });
                setIsOpen(false);
            })
            .catch((ex) => {
                console.log(ex);
                openDialog(
                    <ErrorDialog
                        id={'ErrorDialog'}
                        error={ex}
                        errorMessage={langStrings.errorAdd}
                        resolve={() => {}}
                    />,
                );
            });
    }, [
        title,
        stateStartDate,
        stateEndDate,
        aiGeneratedDuration,
        saving,
        employee,
        dispatch,
        allCertificates,
        resolve,
        openDialog,
        langStrings.errorAdd,
    ]);

    const map = useCallback(() => {
        if (!certId || !allCert) return;
        if (!stateStartDate) return;
        if (saving || !employee) return;
        setSaving(true);
        changeMapCertificate(certId, true, stateStartDate, stateEndDate)
            .then(() => {
                const newCerts = employee.certificates.filter((c) => c.id !== certId);
                newCerts.push({
                    id: certId,
                    title: allCert.title,
                    startDate: stateStartDate,
                    endDate: stateEndDate,
                });
                setSaving(false);
                dispatch(setEmployeeCertificates(newCerts));
                resolve({ success: true, startDate: stateStartDate, endDate: stateEndDate });
                setIsOpen(false);
            })
            .catch((ex) => {
                openDialog(
                    <ErrorDialog
                        id={'ErrorDialog'}
                        error={ex}
                        errorMessage={langStrings.errorAdd}
                        resolve={() => {}}
                    />,
                );
                setSaving(false);
            });
    }, [
        certId,
        allCert,
        stateStartDate,
        stateEndDate,
        saving,
        employee,
        dispatch,
        resolve,
        openDialog,
        langStrings.errorAdd,
    ]);

    const onCancel = useCallback(() => {
        resolve({ success: false });
        setIsOpen(false);
    }, [resolve]);

    const onConfirm = useCallback(() => {
        if (mode === 'datesOnly') {
            resolve({
                success: true,
                startDate: stateStartDate,
                endDate: stateEndDate,
                durationInDays: aiGeneratedDuration || undefined,
            });
            setIsOpen(false);
            return;
        }

        if (certId) {
            map();
        } else {
            add();
        }
    }, [mode, stateStartDate, stateEndDate, aiGeneratedDuration, certId, resolve, map, add]);

    useEffect(() => {
        if (!isOpen) {
            onCancel();
        }
    }, [isOpen, onCancel]);

    if (!isOpen) return null;
    if (!employee || certId === -1) return null;

    return (
        <Dialog
            id={id}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={onCancel} text={langStrings.oftenUsed.cancel} icon={<Close />} size="normal" />
                    <Button
                        color="primary"
                        disabled={saving || !stateStartDate || offline}
                        onClick={onConfirm}
                        text={langStrings.oftenUsed.save}
                        icon={<Check />}
                        size="normal"
                    />
                </div>
            }
            title={own ? langStrings.editCertificateTitle : langStrings.addCertificateTitle}
            onClose={onCancel}
        >
            <Typography variant="body1">{langStrings.addCertDialogHint}</Typography>
            {loadingDuration && <LoadingBox text="Generating certificate duration..." />}
            {certId === undefined && aiGeneratedDuration !== null && (
                <RowElement title="Gültigkeit (Tage)">
                    <input
                        type="number"
                        value={aiGeneratedDuration || ''}
                        onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            setAiGeneratedDuration(Number.isNaN(value) ? null : value);
                        }}
                        style={{ width: '100%', padding: '8px' }}
                    />
                </RowElement>
            )}
            <Row>
                <RowElement title={langStrings.startDate}>
                    <DateInput
                        value={stateStartDate}
                        onChange={(val) => {
                            setStateStartDate(val);
                        }}
                    />
                    {/* <Typography variant='caption'>
                                {langStrings.oftenUsed.today}
                            </Typography> */}
                </RowElement>
                <RowElement title={langStrings.endDate}>
                    <DateInput
                        value={stateEndDate}
                        onChange={(val) => {
                            setStateEndDate(val);
                        }}
                    />
                </RowElement>
            </Row>
            <Typography variant="caption">{langStrings.startDateHint}</Typography>
        </Dialog>
    );
};

export function useMapCertificateDialog() {
    const { openDialog } = useDialog();

    const mapCertificateDialog = useCallback(
        (
            certId?: number,
            title?: string,
            mode: 'full' | 'datesOnly' = 'full',
        ): Promise<IMapCertificateDialogResult> => {
            return new Promise<IMapCertificateDialogResult>((resolve) => {
                const dialog = (
                    <MapCertificateDialog
                        id="mapCertificateDialog"
                        certId={certId}
                        title={title}
                        mode={mode}
                        resolve={resolve}
                    />
                );
                openDialog(dialog);
            });
        },
        [openDialog],
    );

    return mapCertificateDialog;
}
